import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BedDouble, Users, Crown, Hotel as HotelIcon, LayoutGrid, List, Plus } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, patch, del } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Stat,
  Button,
  Modal,
  Field,
  Input,
  Select,
  Table,
  Badge,
  StatusBadge,
  Loader,
  EmptyState,
  SectionTitle,
  Pagination,
  SearchInput,
  Avatar,
  label,
} from '../../components/UI';
import { fmtNumber, pct } from '../../utils/format';

export default function Rooms() {
  const { eventId, events, event } = useEvents();

  const [view, setView] = useState('grid');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [group, setGroup] = useState('');

  const { data: rooms, meta, loading, reload } = useFetch(
    '/rooms',
    { page, limit: view === 'grid' ? 48 : 25, ...(eventId ? { event: eventId } : {}), ...(status ? { status } : {}), ...(group ? { group } : {}) },
    { skip: !eventId, withMeta: true },
  );
  const { data: groups, reload: reloadGroups } = useFetch('/rooms/groups', { event: eventId }, { skip: !eventId });
  // A company admin allocates by hand and never picks a property: the hotel is
  // whichever one the organiser assigned to the event.
  // Nobody picks a property here — a room belongs to whichever hotel the
  // organiser attached to the event.
  const eventHotel = event?.hotels?.[0] || null;

  const [manualOpen, setManualOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);

  const openRoom = (r) => { setEditingRoom(r); setManualOpen(true); };
  const closeRoom = () => { setManualOpen(false); setEditingRoom(null); };
  const { data: participants } = useFetch(
    '/users', { event: eventId, role: 'user', limit: 300 }, { skip: !eventId },
  );

  if (!events.length) {
    return <EmptyState message="No events available" hint="Room allocation begins once you have an event." icon={BedDouble} />;
  }

  const totals = (groups || []).reduce((a, g) => ({ rooms: a.rooms + g.rooms, guests: a.guests + g.guests }), { rooms: 0, guests: 0 });
  const occupied = (rooms || []).filter((r) => r.status === 'occupied').length;
  const unassigned = (rooms || []).filter((r) => !r.roomNumber).length;

  const refreshAll = () => { reload(); reloadGroups(); };

  return (
    <>
      <SectionTitle
        allowViewer
        title="Room allocation"
        subtitle={`Map room partners into groups${eventHotel ? ` at ${eventHotel.name}` : ''} — the hotel assigns the numbers`}
        action={
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <EventPicker />
            <Button icon={Plus} onClick={() => openRoom(null)} disabled={!eventId}>
              Create room
            </Button>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 13, marginBottom: 18 }}>
        <Stat label="Rooms Allocated" value={fmtNumber(totals.rooms)} icon={BedDouble} tone="gold" />
        <Stat label="Guests Placed" value={fmtNumber(totals.guests)} icon={Users} tone="info" />
        <Stat label="Checked In" value={occupied} icon={HotelIcon} tone="success" progress={pct(occupied, rooms?.length || 0)} />
        <Stat label="Awaiting Room No." value={unassigned} icon={Crown} tone="warning" />
      </div>

      <Card padding={15} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 11, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select value={group} onChange={(e) => { setGroup(e.target.value); setPage(1); }} style={{ width: 195 }}>
            <option value="">All groups</option>
            {(groups || []).map((g) => (
              <option key={g.groupName} value={g.groupName}>{g.groupName} ({g.rooms})</option>
            ))}
          </Select>
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ width: 175 }}>
            <option value="">All statuses</option>
            {['vacant', 'reserved', 'occupied', 'checked_out', 'maintenance'].map((s) => (
              <option key={s} value={s}>{label(s)}</option>
            ))}
          </Select>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--ink-800)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}>
            {[['grid', LayoutGrid], ['list', List]].map(([v, Icon]) => (
              <button key={v} onClick={() => setView(v)}
                style={{
                  padding: '6px 11px', borderRadius: 6, display: 'flex', alignItems: 'center',
                  background: view === v ? 'var(--gold-400)' : 'transparent',
                  color: view === v ? 'var(--ink-900)' : 'var(--text-mid)',
                  transition: 'all 0.18s',
                }}>
                <Icon size={14} />
              </button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? <Loader label="Loading room allocations" /> : !rooms?.length ? (
        <EmptyState
          message="No rooms allocated yet"
          hint="Create a room and pick who shares it. The hotel fills in the room number afterwards."
          icon={BedDouble}
          action={<Button icon={Plus} onClick={() => openRoom(null)}>Create the first room</Button>}
        />
      ) : view === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(238px, 1fr))', gap: 13 }}>
          {rooms.map((r, i) => <RoomCard key={r._id} room={r} index={i} onEdit={() => openRoom(r)} />)}
        </div>
      ) : (
        <>
          <Table
            rows={rooms}
            onRowClick={openRoom}
            columns={[
              { key: 'roomNumber', title: 'Room', render: (r) => (
                <span style={{ fontWeight: 600, color: r.roomNumber ? 'var(--gold-400)' : 'var(--text-lo)' }}>
                  {r.roomNumber || 'Not assigned'}
                </span>
              )},
              { key: 'hotel', title: 'Hotel', render: (r) => r.hotel?.name || '—' },
              { key: 'category', title: 'Category', render: (r) => r.category || '—' },
              { key: 'groupName', title: 'Group', render: (r) => <Badge tone="neutral">{r.groupName || 'Unassigned'}</Badge> },
              { key: 'occupants', title: 'Guests', render: (r) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {r.occupants?.map((o) => (
                    <span key={o._id} style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {o.isVip && <Crown size={11} color="var(--gold-400)" />}{o.name}
                    </span>
                  )) || '—'}
                </div>
              )},
              { key: 'status', title: 'Status', render: (r) => <StatusBadge status={r.status} /> },
            ]}
          />
          <Pagination meta={meta} onChange={setPage} />
        </>
      )}

      <ManualRoomModal
        open={manualOpen} onClose={closeRoom}
        room={editingRoom}
        eventId={eventId}
        hotel={eventHotel}
        participants={participants || []}
        allocated={rooms || []}
        onDone={refreshAll}
      />
    </>
  );
}

function RoomCard({ room, index, onEdit }) {
  const full = room.occupants?.length >= room.maxOccupancy;
  const hasVip = room.occupants?.some((o) => o.isVip);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.022, 0.4) }}
    >
      <Card hover onClick={onEdit} padding={15} style={{ height: '100%', cursor: onEdit ? 'pointer' : undefined, borderColor: hasVip ? 'rgba(239,212,138,0.28)' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 9 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{
                fontSize: 19, fontWeight: 700, fontFamily: 'var(--font-display)',
                color: room.roomNumber ? 'var(--gold-300)' : 'var(--text-lo)',
              }}>
                {room.roomNumber || '— — —'}
              </span>
              {hasVip && <Crown size={14} color="var(--gold-400)" />}
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 3 }}>
              {room.category || 'Standard'}{room.floor ? ` · Floor ${room.floor}` : ''}
            </p>
          </div>
          <StatusBadge status={room.status} />
        </div>

        <div style={{ marginTop: 13, paddingTop: 12, borderTop: '1px solid var(--ink-750)' }}>
          {room.occupants?.length ? room.occupants.map((o) => (
            <div key={o._id} style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
              <Avatar name={o.name} size={26} vip={o.isVip} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.name}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-lo)' }}>{o.designation || o.employeeCode || ''}</div>
              </div>
            </div>
          )) : (
            <p style={{ fontSize: 12, color: 'var(--text-lo)' }}>No guests assigned</p>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <Badge tone="neutral">{room.groupName || 'Unassigned'}</Badge>
          <span style={{ fontSize: 11, color: full ? 'var(--ok)' : 'var(--text-lo)' }}>
            {room.occupants?.length || 0}/{room.maxOccupancy}
          </span>
        </div>
      </Card>
    </motion.div>
  );
}

/**
 * Manual allocation: name a group, pick who shares the room. The hotel is not
 * offered — it is whichever one the organiser assigned to the event.
 */
function ManualRoomModal({ open, onClose, eventId, hotel, participants, allocated, room, onDone }) {
  const toast = useToast();
  const [form, setForm] = useState({ groupName: '', maxOccupancy: 2, occupants: [] });
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const editing = Boolean(room);

  /* Load the room being edited, and reset cleanly between openings. */
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setForm(room
      ? {
        groupName: room.groupName || '',
        maxOccupancy: room.maxOccupancy || 2,
        occupants: (room.occupants || []).map((o) => String(o._id || o)),
      }
      : { groupName: '', maxOccupancy: 2, occupants: [] });
  }, [open, room]);

  // Somebody already in a room cannot be put in another — the API refuses it,
  // so they are not offered here either. The people in *this* room obviously
  // stay available while it is being edited.
  const mine = new Set((room?.occupants || []).map((o) => String(o._id || o)));
  const taken = new Set(
    allocated
      .flatMap((r) => (r.occupants || []).map((o) => String(o._id || o)))
      .filter((uid) => !mine.has(uid)),
  );
  const term = search.trim().toLowerCase();
  const available = participants.filter((p) => (
    !taken.has(String(p._id))
    && (!term || `${p.name} ${p.email || ''} ${p.employeeCode || ''}`.toLowerCase().includes(term))
  ));

  const toggle = (uid) => setForm((f) => ({
    ...f,
    occupants: f.occupants.includes(uid) ? f.occupants.filter((x) => x !== uid) : [...f.occupants, uid],
  }));

  const save = async () => {
    if (!form.occupants.length) return toast.error('Add at least one person to the room');
    if (form.occupants.length > Number(form.maxOccupancy)) {
      return toast.error(`You have picked ${form.occupants.length} people for a ${form.maxOccupancy}-person room`);
    }
    setBusy(true);
    try {
      const payload = {
        groupName: form.groupName.trim() || undefined,
        maxOccupancy: Number(form.maxOccupancy),
        occupants: form.occupants,
      };
      if (editing) await patch(`/rooms/${room._id}`, payload);
      else await post('/rooms', { event: eventId, ...payload });
      toast.success(editing ? 'Room updated' : 'Room created');
      onClose();
      onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await del(`/rooms/${room._id}`);
      toast.success('Room removed');
      onClose();
      onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open} onClose={onClose}
      title={editing ? `Room ${room.roomNumber || '— not numbered yet'}` : 'Create a room'}
      subtitle={hotel ? `At ${hotel.name} — the hotel assigns the room number` : 'The hotel assigns the room number'}
      width={560}
      footer={(
        <>
          {editing && (
            <Button variant="subtle" onClick={remove} style={{ marginRight: 'auto', color: 'var(--danger)' }}>
              Remove room
            </Button>
          )}
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={busy}>{editing ? 'Save changes' : 'Create room'}</Button>
        </>
      )}
    >
      {!hotel ? (
        <EmptyState
          message="No hotel assigned to this event yet"
          hint="Your event coordinator assigns the property before rooms can be allocated."
          icon={HotelIcon}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 13 }}>
            <Field label="Group" hint="Optional label, e.g. Sales — Batch A">
              <Input value={form.groupName} onChange={(e) => setForm({ ...form, groupName: e.target.value })} />
            </Field>
            <Field label="Room holds">
              <Select value={form.maxOccupancy} onChange={(e) => setForm({ ...form, maxOccupancy: e.target.value })}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>)}
              </Select>
            </Field>
          </div>

          <Field label={`Room partners — ${form.occupants.length} of ${form.maxOccupancy} picked`} required>
            <SearchInput
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search your employees…" style={{ marginBottom: 9 }}
            />
            <div style={{
              maxHeight: 230, overflowY: 'auto',
              border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: 5,
            }}>
              {!available.length && (
                <p style={{ fontSize: 12.5, color: 'var(--text-lo)', padding: 9 }}>
                  {taken.size && !term ? 'Everyone has been allocated already.' : 'Nobody matches that search.'}
                </p>
              )}
              {available.slice(0, 60).map((p) => {
                const on = form.occupants.includes(p._id);
                return (
                  <label key={p._id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 9px',
                    borderRadius: 'var(--r-sm)', cursor: 'pointer', fontSize: 13,
                    background: on ? 'rgba(239,212,138,0.09)' : 'transparent',
                  }}>
                    <input type="checkbox" checked={on} onChange={() => toggle(p._id)}
                      style={{ accentColor: 'var(--gold-400)' }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>
                        {[p.employeeCode, p.department].filter(Boolean).join(' · ') || p.email}
                      </span>
                    </span>
                    {p.isVip && <Badge tone="gold">VIP</Badge>}
                  </label>
                );
              })}
            </div>
          </Field>
        </div>
      )}
    </Modal>
  );
}
